import {
  createCsvBlob
} from '../csvEncoding'

import {
  csvEscape,
  downloadBlob,
  ensureStatisticsPdfFonts,
  fileStamp,
  format2
} from './common'

const COLORS = [
  [79, 129, 189],
  [111, 161, 111],
  [194, 124, 89],
  [155, 111, 174],
  [191, 166, 90]
]

export const buildEmissionsExportBlock = (
  stats,
  periodText,
  vehicleText,
  t
) => {
  const data = stats?.emissionData || []

  const totalEmissions = data.reduce(
    (sum, item) =>
      sum + Number(item.totalEmissionKg || 0),
    0
  )

  return {
    chartData: data.map((item) => ({
      id: item.id,
      vehicleId: item.vehicleId,
      name: item.name || '',
      licensePlate:
        item.licensePlate ||
        item.spz ||
        '',
      totalKm: Number(item.totalKm || 0),
      emissionsPerKm:
        Number(item.emissionsPerKm || 0),
      totalEmissionKg:
        Number(item.totalEmissionKg || 0)
    })),

    headers: [
      t('TRIPLOG.STATISTICS.VEHICLES.VEHICLE_NAME', {
        defaultValue: 'Vozidlo'
      }),
      t('TRIPLOG.STATISTICS.VEHICLES.LICENSE_PLATE', {
        defaultValue: 'ŠPZ'
      }),
      t('TRIPLOG.STATISTICS.SUMMARY.TOTAL_KM', {
        defaultValue: 'Najazdené km'
      }),
      t('TRIPLOG.STATISTICS.CO2.CO2_PER_KM', {
        defaultValue: 'Emisie g/km'
      }),
      t('TRIPLOG.STATISTICS.CO2.TOTAL_CO2', {
        defaultValue: 'Celkové emisie kg'
      }),
      t('TRIPLOG.STATISTICS.CO2.CO2_SHARE', {
        defaultValue: 'Podiel'
      })
    ],

    rows: data.map((item) => [
      item.name || '',
      item.licensePlate || item.spz || '',
      format2(item.totalKm),
      format2(item.emissionsPerKm),
      format2(item.totalEmissionKg),
      `${
        totalEmissions > 0
          ? format2(
              (
                Number(item.totalEmissionKg || 0) /
                totalEmissions
              ) * 100
            )
          : '0.00'
      }%`
    ]),

    meta: {
      period: periodText,
      vehicle: vehicleText
    }
  }
}

export const exportEmissionsCSV = (block, t) => {
  const titleRows = [
    [
      t('TRIPLOG.STATISTICS.TITLE', {
        defaultValue: 'Štatistiky'
      })
    ],
    [
      t('TRIPLOG.STATISTICS.EMISSIONS_TAB', {
        defaultValue: 'Emisie CO₂'
      })
    ],
    [],
    [
      t('TRIPLOG.STATISTICS.SHOWN_PERIOD', {
        defaultValue: 'Zobrazené obdobie'
      }),
      block?.meta?.period || ''
    ],
    [
      t('TRIPLOG.STATISTICS.SHOWN_VEHICLE', {
        defaultValue: 'Zobrazené vozidlo'
      }),
      block?.meta?.vehicle || ''
    ],
    []
  ]

  const csv = [
    ...titleRows,
    block?.headers || [],
    ...(block?.rows || [])
  ]
    .map((row) =>
      row.map(csvEscape).join(';')
    )
    .join('\n')

  downloadBlob(
    createCsvBlob(csv),
    `emissions_statistics_${fileStamp()}.csv`
  )
}

export const exportEmissionsXLS = async (block, t) => {
  const XLSX = await import('xlsx-js-style')

  const titleRows = [
    [
      t('TRIPLOG.STATISTICS.TITLE', {
        defaultValue: 'Štatistiky'
      })
    ],
    [
      t('TRIPLOG.STATISTICS.EMISSIONS_TAB', {
        defaultValue: 'Emisie CO₂'
      })
    ],
    [],
    [
      t('TRIPLOG.STATISTICS.SHOWN_PERIOD', {
        defaultValue: 'Zobrazené obdobie'
      }),
      block?.meta?.period || ''
    ],
    [
      t('TRIPLOG.STATISTICS.SHOWN_VEHICLE', {
        defaultValue: 'Zobrazené vozidlo'
      }),
      block?.meta?.vehicle || ''
    ],
    []
  ]

  const headers = block?.headers || []
  const rows = block?.rows || []

  const worksheetData = [
    ...titleRows,
    headers,
    ...rows
  ]

  const ws = XLSX.utils.aoa_to_sheet(worksheetData)

  const columnCount = Math.max(
    1,
    ...worksheetData.map((row) => row.length)
  )

  ws['!cols'] = Array.from(
    {
      length: columnCount
    },
    (_, columnIndex) => ({
      wch: columnIndex === 0 ? 24 : 20
    })
  )

  const mainTitleCell = XLSX.utils.encode_cell({
    r: 0,
    c: 0
  })

  if (ws[mainTitleCell]) {
    ws[mainTitleCell].s = {
      font: {
        name: 'Arial',
        sz: 14,
        bold: true
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  }

  const subTitleCell = XLSX.utils.encode_cell({
    r: 1,
    c: 0
  })

  if (ws[subTitleCell]) {
    ws[subTitleCell].s = {
      font: {
        name: 'Arial',
        sz: 11,
        bold: true
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  }

  ;[3, 4].forEach((rowIndex) => {
    const labelCell = XLSX.utils.encode_cell({
      r: rowIndex,
      c: 0
    })

    if (ws[labelCell]) {
      ws[labelCell].s = {
        font: {
          name: 'Arial',
          sz: 10,
          bold: true
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    }

    const valueCell = XLSX.utils.encode_cell({
      r: rowIndex,
      c: 1
    })

    if (ws[valueCell]) {
      ws[valueCell].s = {
        font: {
          name: 'Arial',
          sz: 10
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    }
  })

  const headerRowIndex = titleRows.length

  headers.forEach((_, columnIndex) => {
    const cellAddress = XLSX.utils.encode_cell({
      r: headerRowIndex,
      c: columnIndex
    })

    if (!ws[cellAddress]) return

    ws[cellAddress].s = {
      font: {
        name: 'Arial',
        sz: 10,
        bold: true
      },
      fill: {
        fgColor: {
          rgb: 'F0F0F0'
        }
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  })

  rows.forEach((row, rowIndex) => {
    row.forEach((_, columnIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: headerRowIndex + 1 + rowIndex,
        c: columnIndex
      })

      if (!ws[cellAddress]) return

      ws[cellAddress].s = {
        font: {
          name: 'Arial',
          sz: 10
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    })
  })

  const wb = XLSX.utils.book_new()

  XLSX.utils.book_append_sheet(
    wb,
    ws,
    t('TRIPLOG.STATISTICS.EMISSIONS_TAB', {
      defaultValue: 'Emisie CO₂'
    })
  )

  XLSX.writeFile(
    wb,
    `emissions_statistics_${fileStamp()}.xlsx`
  )
}

const drawDonutChart = ({
  doc,
  data,
  centerX,
  centerY,
  outerRadius,
  innerRadius,
  total,
  t
}) => {
  const labelItems = []

  if (!data.length || total <= 0) {
    doc.setDrawColor(230, 230, 230)
    doc.setLineWidth(outerRadius - innerRadius)

    doc.circle(
      centerX,
      centerY,
      (outerRadius + innerRadius) / 2,
      'S'
    )
  } else {
    let startAngle = -90

    data.forEach((item, index) => {
      const value =
        Number(item.totalEmissionKg || 0)

      const angleSize =
        total > 0
          ? (value / total) * 360
          : 0

      const endAngle =
        startAngle + angleSize

      const middleAngle =
        startAngle + angleSize / 2

      const [r, g, b] =
        COLORS[index % COLORS.length]

      doc.setDrawColor(r, g, b)
      doc.setLineWidth(
        outerRadius - innerRadius + 1
      )

      const middleRadius =
        (outerRadius + innerRadius) / 2

      const step = 0.5

      for (
        let angle = startAngle;
        angle < endAngle;
        angle += step
      ) {
        const radians =
          (angle * Math.PI) / 180

        const x =
          centerX +
          Math.cos(radians) * middleRadius

        const y =
          centerY +
          Math.sin(radians) * middleRadius

        doc.line(
          centerX,
          centerY,
          x,
          y
        )
      }

      if (value > 0) {
        const percentage =
          total > 0
            ? (value / total) * 100
            : 0

        const middleRadians =
          (middleAngle * Math.PI) / 180

        const lineStartX =
          centerX +
          Math.cos(middleRadians) *
            (outerRadius - 8)

        const lineStartY =
          centerY +
          Math.sin(middleRadians) *
            (outerRadius - 8)

        const lineBreakX =
          centerX +
          Math.cos(middleRadians) *
            (outerRadius + 22)

        const lineBreakY =
          centerY +
          Math.sin(middleRadians) *
            (outerRadius + 22)

        const isRightSide =
          Math.cos(middleRadians) >= 0

        const labelX =
          lineBreakX +
          (isRightSide ? 16 : -16)

        const label = `${
          item.name || ''
        }${
          item.licensePlate
            ? ` (${item.licensePlate})`
            : ''
        }`

        const valueText =
          `${format2(value)} ${t(
            'TRIPLOG.UNITS.KG',
            {
              defaultValue: 'kg'
            }
          )} • ${format2(percentage)}%`

        labelItems.push({
          r,
          g,
          b,
          lineStartX,
          lineStartY,
          lineBreakX,
          lineBreakY,
          labelX,
          labelY: lineBreakY,
          isRightSide,
          label,
          valueText
        })
      }

      startAngle = endAngle
    })
  }

  doc.setFillColor(255, 255, 255)

  doc.circle(
    centerX,
    centerY,
    innerRadius,
    'F'
  )

  doc.setFont('Roboto', 'bold')
  doc.setFontSize(12)
  doc.setTextColor(13, 95, 83)

  doc.text(
    `${format2(total)} ${t(
      'TRIPLOG.UNITS.KG',
      {
        defaultValue: 'kg'
      }
    )}`,
    centerX,
    centerY + 4,
    {
      align: 'center'
    }
  )

  const leftLabels = labelItems
    .filter((item) => !item.isRightSide)
    .sort((a, b) => a.labelY - b.labelY)

  const rightLabels = labelItems
    .filter((item) => item.isRightSide)
    .sort((a, b) => a.labelY - b.labelY)

  const spreadLabels = (items) => {
    const minGap = 35

    items.forEach((item, index) => {
      if (index === 0) return

      const previous = items[index - 1]

      if (
        item.labelY - previous.labelY <
        minGap
      ) {
        item.labelY =
          previous.labelY + minGap
      }
    })

    for (
      let index = items.length - 2;
      index >= 0;
      index -= 1
    ) {
      const current = items[index]
      const next = items[index + 1]

      if (
        next.labelY - current.labelY <
        minGap
      ) {
        current.labelY =
          next.labelY - minGap
      }
    }
  }

  spreadLabels(leftLabels)
  spreadLabels(rightLabels)

  ;[...leftLabels, ...rightLabels].forEach(
    (item) => {
      doc.setDrawColor(
        item.r,
        item.g,
        item.b
      )

      doc.setLineWidth(1.2)

      doc.line(
        item.lineStartX,
        item.lineStartY,
        item.lineBreakX,
        item.lineBreakY
      )

      doc.line(
        item.lineBreakX,
        item.lineBreakY,
        item.labelX,
        item.labelY
      )

      doc.setFont('Roboto', 'bold')
      doc.setFontSize(8)

      const labelWidth = Math.max(
        doc.getTextWidth(item.label),
        doc.getTextWidth(item.valueText)
      )

      const bubbleWidth =
        labelWidth + 16

      const bubbleHeight = 28

      const bubbleX =
        item.isRightSide
          ? item.labelX
          : item.labelX - bubbleWidth

      const bubbleY =
        item.labelY - bubbleHeight / 2

      doc.setFillColor(255, 255, 255)

      doc.setDrawColor(
        item.r,
        item.g,
        item.b
      )

      doc.roundedRect(
        bubbleX,
        bubbleY,
        bubbleWidth,
        bubbleHeight,
        6,
        6,
        'FD'
      )

      const textX =
        item.isRightSide
          ? bubbleX + 8
          : bubbleX + bubbleWidth - 8

      const align =
        item.isRightSide
          ? 'left'
          : 'right'

      doc.setTextColor(
        item.r,
        item.g,
        item.b
      )

      doc.text(
        item.label,
        textX,
        bubbleY + 11,
        {
          align
        }
      )

      doc.setFont('Roboto', 'normal')
      doc.setFontSize(7)
      doc.setTextColor(0, 0, 0)

      doc.text(
        item.valueText,
        textX,
        bubbleY + 21,
        {
          align
        }
      )
    }
  )
}

const drawDonutLegend = ({
  doc,
  data,
  total,
  startX,
  startY,
  maxWidth,
  t
}) => {
  let currentX = startX
  let currentY = startY
  const itemHeight = 22

  doc.setFont('Roboto', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(0, 0, 0)

  data.forEach((item, index) => {
    const value =
      Number(item.totalEmissionKg || 0)

    const percentage =
      total > 0
        ? (value / total) * 100
        : 0

    const label = `${
      item.name || ''
    }${
      item.licensePlate
        ? ` (${item.licensePlate})`
        : ''
    }`

    const valueText =
      `${format2(value)} ${t('TRIPLOG.UNITS.KG', {
        defaultValue: 'kg'
      })} • ${format2(percentage)}%`

    const labelWidth = Math.max(
      doc.getTextWidth(label),
      doc.getTextWidth(valueText)
    )

    const itemWidth =
      labelWidth + 34

    if (
      currentX + itemWidth >
      startX + maxWidth
    ) {
      currentX = startX
      currentY += itemHeight + 14
    }

    const [r, g, b] =
      COLORS[index % COLORS.length]

    doc.setFillColor(r, g, b)

    doc.rect(
      currentX,
      currentY - 8,
      10,
      10,
      'F'
    )

    doc.setFont('Roboto', 'bold')
    doc.setFontSize(8)
    doc.setTextColor(r, g, b)

    doc.text(
      label,
      currentX + 15,
      currentY
    )

    doc.setFont('Roboto', 'normal')
    doc.setFontSize(7)
    doc.setTextColor(0, 0, 0)

    doc.text(
      valueText,
      currentX + 15,
      currentY + 10
    )

    currentX += itemWidth
  })

  return currentY + 16
}

export const exportEmissionsPDF = async (block, t) => {
  const { jsPDF } = await import('jspdf')
  const autoTableModule = await import('jspdf-autotable')

  const autoTable =
    autoTableModule.default ||
    autoTableModule.autoTable

  const headers = block?.headers || []
  const rows = block?.rows || []
  const chartData = block?.chartData || []

  const totalEmissions = chartData.reduce(
    (sum, item) =>
      sum + Number(item.totalEmissionKg || 0),
    0
  )

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4'
  })

  await ensureStatisticsPdfFonts(doc)

  const marginLeft = 18
  const marginRight = 18
  const pageWidth =
    doc.internal.pageSize.getWidth()

  const usableWidth =
    pageWidth -
    marginLeft -
    marginRight

  const columnWidth =
    usableWidth /
    Math.max(headers.length, 1)

  doc.setFont('Roboto', 'bold')
  doc.setFontSize(14)
  doc.setTextColor(0, 0, 0)

  doc.text(
    t('TRIPLOG.STATISTICS.TITLE', {
      defaultValue: 'Štatistiky'
    }),
    marginLeft,
    28
  )

  doc.setFontSize(11)

  doc.text(
    t('TRIPLOG.STATISTICS.EMISSIONS_TAB', {
      defaultValue: 'Emisie CO₂'
    }),
    marginLeft,
    44
  )

  doc.setFontSize(9)

  doc.text(
    `${t('TRIPLOG.STATISTICS.SHOWN_PERIOD', {
      defaultValue: 'Zobrazené obdobie'
    })}: ${block?.meta?.period || ''}`,
    marginLeft,
    60
  )

  doc.text(
    `${t('TRIPLOG.STATISTICS.SHOWN_VEHICLE', {
      defaultValue: 'Zobrazené vozidlo'
    })}: ${block?.meta?.vehicle || ''}`,
    marginLeft,
    74
  )

  const donutCenterX = pageWidth / 2
  const donutCenterY = 180
  const donutOuterRadius = 82
  const donutInnerRadius = 48

  drawDonutChart({
    doc,
    data: chartData,
    centerX: donutCenterX,
    centerY: donutCenterY,
    outerRadius: donutOuterRadius,
    innerRadius: donutInnerRadius,
    total: totalEmissions,
    t
  })

  const legendBottom = drawDonutLegend({
    doc,
    data: chartData,
    total: totalEmissions,
    startX: marginLeft,
    startY: 292,
    maxWidth: usableWidth,
    t
  })

  const tableStartY =
    Math.max(
      330,
      legendBottom + 12
    )

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: tableStartY,
    theme: 'grid',

    margin: {
      top: 18,
      right: marginRight,
      bottom: 18,
      left: marginLeft
    },

    tableWidth: usableWidth,

    styles: {
      font: 'Roboto',
      fontStyle: 'normal',
      fontSize: 8,
      cellPadding: 3,
      overflow: 'linebreak',
      valign: 'middle',
      halign: 'left',
      lineWidth: 0.3,
      lineColor: [0, 0, 0],
      minCellHeight: 12
    },

    headStyles: {
      font: 'Roboto',
      fontStyle: 'bold',
      fontSize: 8,
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      lineWidth: 0.4,
      lineColor: [0, 0, 0]
    },

    bodyStyles: {
      font: 'Roboto',
      fontStyle: 'normal',
      textColor: [0, 0, 0],
      lineWidth: 0.3,
      lineColor: [0, 0, 0]
    },

    columnStyles: headers.reduce(
      (acc, _header, index) => {
        acc[index] = {
          cellWidth: columnWidth
        }

        return acc
      },
      {}
    ),

    horizontalPageBreak: false,
    pageBreak: 'auto',
    rowPageBreak: 'avoid'
  })

  downloadBlob(
    doc.output('blob'),
    `emissions_statistics_${fileStamp()}.pdf`
  )
}